import React, { ChangeEvent, FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { useReportViewEvent, useResponsive } from '../../hooks'
import { Progress } from '../../@types/progress'
import { TextInput } from '../../components/styled/TextInput/TextInput'
import { ReduxState } from '../../store/reducers'
import { updateIncome } from '../../store/lead/leadSlice'
import { reportTrackingEvent } from '../../tracking/thunks'
import { IncomeIcon } from '../../components/icons/IncomeIcon'

export interface IncomeStepProps {
  goToNextStep: () => unknown
  goToPreviousStep: () => unknown
  progress: Progress
  displayBack: boolean
}

export const isIncomeWithinRange = (inputValue: number | undefined): boolean => {
  return inputValue !== undefined && inputValue >= 1000 && inputValue <= 999999
}

export const IncomeStep: FC<IncomeStepProps> = (props: IncomeStepProps) => {
  useReportViewEvent({ pageTitle: 'income' })
  const {
    config: { theme },
    lead: { income },
  } = useSelector((state: ReduxState) => state)
  const { isMobile } = useResponsive()

  const dispatch = useDispatch()
  const [inputValue, setInputValue] = useState(income)
  const [inputActionPerformed, setInputActionPerformed] = useState(false)

  const onInputValue = (event: ChangeEvent<HTMLInputElement>) => {
    if (!inputActionPerformed) {
      setInputActionPerformed(true)
    }
    const value = event.target.value
    const number = Number(value)
    if (value === '') {
      setInputValue(undefined)
    } else if (!isNaN(number) && value.length <= 6) {
      setInputValue(number)
    }
  }

  const goToNextStep = () => {
    if (inputValue != null) {
      dispatch(updateIncome(inputValue))
    }

    dispatch(
      reportTrackingEvent({
        label: 'rle-income_submit',
        action: 'realtor-leadengine',
        category: 'residential',
        pageTitle: 'income',
        inputValue: `${inputValue}`,
        eventName: 'lead_input',
      })
    )
    if (props.goToNextStep) {
      props.goToNextStep()
    }
  }

  return (
    <StepTemplate
      icon={<IncomeIcon />}
      headline={<span>Wie hoch sind deine Einnahmen?</span>}
      displayNext={true}
      isNextEnabled={isIncomeWithinRange(inputValue)}
      goToNextStep={goToNextStep}
      goToPreviousStep={props.goToPreviousStep}
      dataTestingButton={'income-next-button'}
      progress={props.progress}
      displayBack={props.displayBack}
      backgroundColor={theme.background}
      textColor={theme.text}
    >
      <div className='grid-flex grid-justify-center'>
        <div
          className='grid-item one-whole align-left area-container input-error-fade area-step'
          style={{ maxWidth: isMobile ? '100%' : '300px' }}
        >
          <TextInput
            id='income'
            data-testing='income-input'
            data-testid='income-input'
            placeholder=''
            label='Monatliches Gesamtnettoeinkommen'
            type='number'
            maxLength={6}
            className={'input-text'}
            value={inputValue}
            onChange={onInputValue}
            autoFocus
            textColor={theme.text}
            borderColor={theme.secondary}
            backgroundColor={theme.background}
            inputMode='numeric'
          />
        </div>
      </div>
    </StepTemplate>
  )
}

export default IncomeStep
